import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { EASE } from "./ui/motion";
import ProfileImage from "../image/portfolio-black-removebg-preview.png";
import BurgerImg from "../image/burgerimg.jpg";
import DoctorImg from "../image/doctorsoffice.jpg";
import LastStandImg from "../image/thelastsantd.jpg";
import OutdoorShootingImg from "../image/outdoorshooting.png";
import Meta from "../image/CERTIFICATE_LANDING_PAGE~GNFRPR5QHT4W.jpeg";
import Udemy from "../image/UDEMY REACT.jpg";
import Nextjs from "../image/nextjs.jpg";
import Git from "../image/Git and Github.png";

const ASSETS = [
  ProfileImage,
  BurgerImg,
  DoctorImg,
  LastStandImg,
  OutdoorShootingImg,
  Meta,
  Udemy,
  Nextjs,
  Git,
];

/* Fetch every image up front so the journey never pops in
   half-loaded. Resolves on error too — a broken image must
   not trap the visitor on the loader. */
function preloadAssets(urls, timeoutMs = 3500) {
  const all = Promise.all(
    urls.map(
      (src) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = resolve;
          img.src = src;
        })
    )
  );
  const timeout = new Promise((resolve) => setTimeout(resolve, timeoutMs));
  return Promise.race([all, timeout]);
}

export default function Preloader({ onComplete }) {
  const [done, setDone] = useState(false);
  const countRef = useRef(null);
  const barRef = useRef(null);

  /* The counter writes straight to the DOM — no setState per
     frame, so React never re-renders during the animation.
     This is what made the old loader stutter on weak CPUs. */
  useEffect(() => {
    let raf;
    let assetsReady = false;
    preloadAssets(ASSETS).then(() => {
      assetsReady = true;
    });

    const start = performance.now();
    const duration = 1900;

    const paint = (value) => {
      if (countRef.current) countRef.current.textContent = String(value).padStart(3, "0");
      if (barRef.current) barRef.current.style.width = `${value}%`;
    };

    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      // ease-out so the number decelerates into 100
      const eased = 1 - Math.pow(1 - p, 3);
      // hold just under 100 until the images are actually in
      const cap = assetsReady ? 100 : 94;
      paint(Math.min(Math.round(eased * 100), cap));

      if (p < 1 || !assetsReady) {
        raf = requestAnimationFrame(tick);
      } else {
        paint(100);
        setTimeout(() => setDone(true), 350);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <AnimatePresence onExitComplete={onComplete}>
      {!done && (
        <motion.div
          className="fixed inset-0 z-[10000] bg-ink flex flex-col justify-between p-6 sm:p-10"
          exit={{ y: "-100%" }}
          transition={{ duration: 1, ease: EASE }}
        >
          {/* top meta */}
          <motion.div
            className="flex justify-between items-center font-mono text-[11px] uppercase tracking-ultra text-paper-dim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <span>Portfolio</span>
            <span>2026 ©</span>
          </motion.div>

          {/* center name */}
          <div className="flex flex-col items-start">
            <div className="overflow-hidden">
              <motion.h1
                className="display text-[14vw] sm:text-[11vw] leading-none text-paper"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.15, ease: EASE }}
              >
                Tsiakiris <span className="italic font-light text-acid">Nikos</span>
              </motion.h1>
            </div>
          </div>

          {/* bottom counter + progress bar */}
          <div className="flex flex-col gap-4">
            <div className="h-px w-full bg-paper-faint overflow-hidden">
              <div ref={barRef} className="h-full bg-acid" style={{ width: "0%" }} />
            </div>
            <div className="flex justify-between items-end">
              <span className="font-mono text-[11px] uppercase tracking-ultra text-paper-dim">
                Loading experience
              </span>
              <span ref={countRef} className="display text-5xl sm:text-7xl tabular-nums">
                000
              </span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
